import React from 'react'
import { Card, Col, Divider, List, Row, Tag, Typography } from 'antd'
import {
  BulbOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  FallOutlined,
  RightCircleOutlined,
  RiseOutlined,
} from '@ant-design/icons'

const { Paragraph, Text, Title } = Typography

export interface NarrativeProduct {
  nombre: string
  monto_maximo: string
  requisito_clave: string
  accion_concreta: string
}

export interface NarrativeAlternatives {
  mensaje: string
  productos: NarrativeProduct[]
  pasos_sugeridos: string[]
  nota_cierre: string
}

export interface LLMNarrative {
  resumen_ejecutivo: string
  explicacion_decision: string
  factores_favorables: string[]
  factores_de_riesgo: string[]
  alternativas: NarrativeAlternatives | null
}

const DECISION_PALETTE: Record<string, { bg: string; border: string; accent: string; tag: string }> = {
  APPROVED:       { bg: '#f6ffed', border: '#b7eb8f', accent: '#389e0d', tag: 'success' },
  REJECTED:       { bg: '#fff2f0', border: '#ffccc7', accent: '#cf1322', tag: 'error' },
  PENDING_REVIEW: { bg: '#fffbe6', border: '#ffe58f', accent: '#d48806', tag: 'warning' },
  PROCESSING:     { bg: '#e6f7ff', border: '#91d5ff', accent: '#096dd9', tag: 'processing' },
}

function getPalette(decision: string) {
  return DECISION_PALETTE[decision?.toUpperCase()] ?? DECISION_PALETTE['PENDING_REVIEW']
}

interface LLMNarrativeCardProps {
  narrative: LLMNarrative
  decision: string
}

const LLMNarrativeCard: React.FC<LLMNarrativeCardProps> = ({ narrative, decision }) => {
  const palette = getPalette(decision)

  return (
    <Card
      style={{
        marginBottom: 24,
        border: `1.5px solid ${palette.border}`,
        background: palette.bg,
        borderRadius: 10,
      }}
      styles={{ body: { padding: '20px 24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <BulbOutlined style={{ fontSize: 22, color: palette.accent }} />
        <Title level={4} style={{ margin: 0, color: palette.accent }}>
          Análisis Asistido por IA
        </Title>
        <Tag color={palette.tag} style={{ marginLeft: 'auto', fontSize: 12 }}>
          {decision}
        </Tag>
      </div>

      <Paragraph style={{ fontSize: 14, lineHeight: 1.7, color: '#1a1a1a', marginBottom: 16 }}>
        {narrative.resumen_ejecutivo}
      </Paragraph>

      <div
        style={{
          background: 'rgba(0,0,0,0.04)',
          borderRadius: 6,
          padding: '12px 16px',
          marginBottom: 16,
          borderLeft: `3px solid ${palette.accent}`,
        }}
      >
        <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666' }}>
          Por qué esta decisión
        </Text>
        <Paragraph style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: '#333', lineHeight: 1.65 }}>
          {narrative.explicacion_decision}
        </Paragraph>
      </div>

      <Row gutter={[16, 8]}>
        {narrative.factores_favorables?.length > 0 && (
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <RiseOutlined style={{ color: '#52c41a' }} />
              <Text strong style={{ fontSize: 12, color: '#389e0d' }}>FACTORES FAVORABLES</Text>
            </div>
            <List
              size="small"
              dataSource={narrative.factores_favorables}
              renderItem={item => (
                <List.Item style={{ padding: '3px 0', border: 'none' }}>
                  <Text style={{ fontSize: 12, color: '#555' }}>
                    <CheckOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                    {item}
                  </Text>
                </List.Item>
              )}
            />
          </Col>
        )}
        {narrative.factores_de_riesgo?.length > 0 && (
          <Col xs={24} md={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <FallOutlined style={{ color: '#f5222d' }} />
              <Text strong style={{ fontSize: 12, color: '#cf1322' }}>FACTORES DE RIESGO</Text>
            </div>
            <List
              size="small"
              dataSource={narrative.factores_de_riesgo}
              renderItem={item => (
                <List.Item style={{ padding: '3px 0', border: 'none' }}>
                  <Text style={{ fontSize: 12, color: '#555' }}>
                    <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 6 }} />
                    {item}
                  </Text>
                </List.Item>
              )}
            />
          </Col>
        )}
      </Row>

      {narrative.alternativas && (
        <>
          <Divider style={{ margin: '16px 0', borderColor: palette.border }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <RightCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} />
            <Text strong style={{ color: '#1677ff', fontSize: 13 }}>
              Productos Alternativos Disponibles
            </Text>
          </div>

          <Paragraph style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
            {narrative.alternativas.mensaje}
          </Paragraph>

          {narrative.alternativas.productos?.length > 0 && (
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              {narrative.alternativas.productos.map((prod, idx) => (
                <Col xs={24} sm={12} key={idx}>
                  <div
                    style={{
                      background: '#fff',
                      border: '1px solid #d9d9d9',
                      borderRadius: 8,
                      padding: '12px 14px',
                    }}
                  >
                    <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                      {prod.nombre}
                    </Text>
                    <Tag color="blue" style={{ marginBottom: 8, fontSize: 11 }}>
                      Hasta {prod.monto_maximo}
                    </Tag>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 11, color: '#888' }}>Requisito: </Text>
                      {prod.requisito_clave}
                    </div>
                    <div style={{ fontSize: 12, color: '#1677ff' }}>
                      <Text strong style={{ fontSize: 11, color: '#888' }}>Acción: </Text>
                      {prod.accion_concreta}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {narrative.alternativas.pasos_sugeridos?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>
                PASOS SUGERIDOS
              </Text>
              {narrative.alternativas.pasos_sugeridos.map((paso, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                  <div
                    style={{
                      minWidth: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#1677ff',
                      color: '#fff',
                      fontSize: 11,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <Text style={{ fontSize: 12, color: '#444', lineHeight: '20px' }}>{paso}</Text>
                </div>
              ))}
            </div>
          )}

          {narrative.alternativas.nota_cierre && (
            <Paragraph
              style={{
                fontSize: 12,
                color: '#666',
                fontStyle: 'italic',
                marginBottom: 0,
                borderTop: `1px solid ${palette.border}`,
                paddingTop: 10,
                marginTop: 8,
              }}
            >
              {narrative.alternativas.nota_cierre}
            </Paragraph>
          )}
        </>
      )}
    </Card>
  )
}

export default LLMNarrativeCard
